import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, filter, lastValueFrom, map, of, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { IUser, IUserSettings, Roles, IPaginatedQueryResult } from '@shs-digi-care-monorepo/interfaces';
import { AuthService } from '../auth';
import { CacheService } from './cache.service';

export const RuleEngineServiceName = 'RuleEngineService';
export const SystemServiceNames = [RuleEngineServiceName] as const;

export interface BaseUserUpdatePayload {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    preferredLanguage?: string;
    country?: string;
    state?: string;
}

export type ICreateOrUpdateUserPayload = {
    gid: string;
    roles: string[];
    firstName: string;
    lastName: string;
    email: string;
    teams?: string[];
} & BaseUserUpdatePayload;

export type UserPaginated<T> = IPaginatedQueryResult & {
    data: T[];
};

export interface PatchUserSettings {
    stateTransitionNotificationsEnabled?: boolean;
    commentNotificationsEnabled?: boolean;
    reminderNotificationsEnabled?: boolean;
    emailHidingEnabled?: boolean;
    defaultPageSize?: number;
}

/**
 * Service for managing users via API.
 */
@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly httpClient = inject(HttpClient);
    private readonly authService = inject(AuthService);
    private readonly baseUrl = `${environment.apiUrl}`;
    private readonly cache = new CacheService<IUser>();

    /**
     * Fetches the current authenticated user.
     * @returns Observable of the current user
     */
    public getMe(): Observable<IUser | undefined> {
        return this.cache.getOrSet(
            'me',
            this.httpClient.get<IUser>(`${this.baseUrl}/me`).pipe(
                catchError((error) => {
                    if (error.message) {
                        console.error(error.message);
                    }
                    this.authService.logout();
                    throw error;
                })
            )
        );
    }

    /**
     * Invalidates the cached "me" user data.
     */
    public invalidateMeCache(): void {
        this.cache.invalidate('me');
    }

    /**
     * Updates the current authenticated user.
     * @param patch Partial user data to update
     * @returns Observable of the updated user
     */
    public patchMe(patch: Partial<BaseUserUpdatePayload>): Observable<IUser> {
        return this.httpClient
            .patch<IUser>(`${this.baseUrl}/me`, patch)
            .pipe(tap((user) => this.cache.set('me', user)));
    }

    /**
     * Fetches a user by GID.
     * @param gid The user GID
     * @returns Observable of the user
     */
    public getByGID(gid: string): Observable<IUser> {
        if (gid === RuleEngineServiceName) {
            return of({} as IUser);
        }

        return this.cache.getOrSet(
            gid,
            this.httpClient.get<IUser>(`${this.baseUrl}/users/${gid}`).pipe(
                filter((user) => !!user),
                catchError((error) => {
                    if (error.message) {
                        console.error(error.message);
                    }
                    return of({} as IUser);
                })
            )
        );
    }

    /**
     * Fetches user names from API for multiple GIDs.
     * @param gids List of user GIDs for which to fetch usernames from the API
     * @returns Observable of array containing gid, firstName, lastName
     */
    public getUsernamesByGIDs(gids: string[]): Observable<Pick<IUser, 'gid' | 'firstName' | 'lastName'>[]> {
        if (gids.length === 0) {
            return of([]);
        }
        // Exclude system service names from API call
        const requestBody = {
            gids: gids.filter((gid) => !SystemServiceNames.includes(gid as (typeof SystemServiceNames)[number]))
        };
        return this.httpClient.post<IUser[]>(`${this.baseUrl}/users/get-usernames`, requestBody);
    }

    /**
     * Fetches a paginated list of users with optional filters.
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @param searchTerm Optional search term
     * @param roles Optional array of roles to filter by
     * @param teamId Optional team ID to filter by
     * @param availableFrom Optional availability start date
     * @param availableTo Optional availability end date
     * @returns Observable of paginated users
     */
    public getUsers(
        page: number,
        pageSize: number,
        searchTerm?: string,
        roles?: Roles[],
        teamId?: string,
        availableFrom?: string,
        availableTo?: string
    ): Observable<UserPaginated<IUser>> {
        let params = new HttpParams().append('page', page).append('pageSize', pageSize);

        if (searchTerm) {
            params = params.append('searchTerm', searchTerm);
        }
        if (roles && roles.length > 0) {
            params = params.append('roles', roles.join(','));
        }
        if (teamId) {
            params = params.append('teamId', teamId);
        }
        if (availableFrom) {
            params = params.append('availableFrom', availableFrom);
        }
        if (availableTo) {
            params = params.append('availableTo', availableTo);
        }

        return this.httpClient
            .get<{
                items: IUser[];
                metadata: {
                    itemCount: number;
                    isZeroBased: boolean;
                    take: number;
                    currentPage: number;
                    lastPage: number;
                    hasNextPage: boolean;
                };
            }>(`${this.baseUrl}/users`, { params })
            .pipe(
                map((response) => ({
                    data: response.items || [],
                    page: response.metadata?.currentPage ?? page,
                    pageSize: response.metadata?.take ?? pageSize,
                    total: response.metadata?.itemCount ?? 0
                }))
            );
    }

    /**
     * Fetches all users for a specific team.
     * @param teamId The team ID
     * @returns Observable of users
     */
    public getAllUsersByTeam(teamId: string): Observable<IUser[]> {
        return this.httpClient.get<IUser[]>(`${this.baseUrl}/users/team/${teamId}`);
    }

    /**
     * Fetches a user by their GID.
     * @param gid The user GID
     * @returns Observable of the user
     */
    public getUserByGid(gid: string): Observable<IUser> {
        return this.httpClient.get<IUser>(`${this.baseUrl}/users/${gid}`);
    }

    /**
     * Fetch user by email
     * @param email The email of the user
     * @returns Observable of the user
     */
    public getUserByEmail(email: string): Observable<IUser> {
        return this.getUsers(0, 1, email).pipe(
            map((result) => {
                const users = result.data || [];
                return users?.[0] ?? undefined;
            })
        );
    }

    /**
     * Creates a new user.
     * @param user User data
     * @returns Promise of the created user
     */
    public async createUser(user: ICreateOrUpdateUserPayload): Promise<IUser> {
        return await lastValueFrom(this.httpClient.post<IUser>(`${this.baseUrl}/users`, user));
    }

    /**
     * Updates an existing user.
     * @param user User data with gid
     * @returns Observable of the updated user
     */
    public updateUser(user: ICreateOrUpdateUserPayload): Observable<IUser> {
        const { gid, ...userBody } = user;
        return this.httpClient.patch<IUser>(`${this.baseUrl}/users/${gid}`, userBody);
    }

    /**
     * Deletes a user by their GID.
     * @param gid The user GID
     * @returns Observable of void
     */
    public deleteUser(gid: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/users/${gid}`);
    }
    /**
     * Fetches user settings for the current user.
     * @returns Observable of user settings
     */
    public getUserSettings(): Observable<IUserSettings> {
        return this.httpClient.get<IUserSettings>(`${this.baseUrl}/me/settings`);
    }

    /**
     * Updates user settings for the current user.
     * @param settings Partial user settings to update
     * @returns Observable of the updated user settings
     */
    public updateUserSettings(settings: PatchUserSettings): Observable<IUserSettings> {
        return this.httpClient.patch<IUserSettings>(`${this.baseUrl}/me/settings`, settings);
    }
}
